// Token cost calculator for different OpenAI models
// Prices are per 1M tokens (updated as of 2025)

interface ModelPricing {
  input: number;  // Cost per 1M input tokens
  output: number; // Cost per 1M output tokens
}

const MODEL_PRICING: Record<string, ModelPricing> = {
  'gpt-4o-mini': { input: 0.15, output: 0.60 },
  'gpt-4o': { input: 2.50, output: 10.00 },
  'gpt-4.1-mini-2025-04-14': { input: 0.40, output: 1.60 },
  'gpt-4.1-2025-04-14': { input: 2.50, output: 10.00 },
  'gpt-5-nano-2025-08-07': { input: 0.20, output: 0.80 },
  'gpt-5-mini-2025-08-07': { input: 1.00, output: 4.00 },
  'gpt-5-2025-08-07': { input: 5.00, output: 15.00 },
  'o4-mini-2025-04-16': { input: 1.10, output: 4.40 },
  'o3-2025-04-16': { input: 10.00, output: 40.00 },
};

// Rough estimate: ~4 characters per token for English text
const CHARS_PER_TOKEN = 4;

// Overhead estimates for complete request
const OVERHEAD_ESTIMATES = {
  SYSTEM_MESSAGE: 150, // System message tokens
  MCP_TOOLS: 6500, // MCP tool definitions are large (~5000-8000 tokens)
  DATABASE_INSTRUCTION_BASE: 100, // Base instruction overhead
  DATABASE_INSTRUCTION_PER_DB: 30, // Additional tokens per connected database
  CONVERSATION_MESSAGE_OVERHEAD: 10, // Overhead per historical message
};

export const estimateTokens = (text: string): number => {
  return Math.ceil(text.length / CHARS_PER_TOKEN);
};

export const calculateInputCost = (
  text: string, 
  model: string, 
  options?: {
    conversationHistoryLength?: number;
    databaseCount?: number;
  }
): number => {
  const pricing = MODEL_PRICING[model];
  if (!pricing) return 0;
  
  // Base user input tokens
  const userInputTokens = estimateTokens(text);
  
  // Add overhead estimates
  const systemOverhead = OVERHEAD_ESTIMATES.SYSTEM_MESSAGE;
  const mcpOverhead = OVERHEAD_ESTIMATES.MCP_TOOLS;
  const dbOverhead = OVERHEAD_ESTIMATES.DATABASE_INSTRUCTION_BASE + 
    (options?.databaseCount || 0) * OVERHEAD_ESTIMATES.DATABASE_INSTRUCTION_PER_DB;
  const conversationOverhead = (options?.conversationHistoryLength || 0) * 
    OVERHEAD_ESTIMATES.CONVERSATION_MESSAGE_OVERHEAD;
  
  const totalEstimatedTokens = userInputTokens + systemOverhead + mcpOverhead + 
    dbOverhead + conversationOverhead;
  
  return (totalEstimatedTokens / 1_000_000) * pricing.input;
};

export const calculateOutputCostPerToken = (model: string): number => {
  const pricing = MODEL_PRICING[model];
  if (!pricing) return 0;
  
  return pricing.output / 1_000_000;
};

export const calculateMaxOutputCost = (maxTokens: number, model: string): number => {
  const pricing = MODEL_PRICING[model];
  if (!pricing) return 0;
  
  return (maxTokens / 1_000_000) * pricing.output;
};

export const calculateActualCost = (
  inputTokens: number,
  outputTokens: number,
  reasoningTokens: number,
  model: string
): number => {
  const pricing = MODEL_PRICING[model];
  if (!pricing) return 0;
  
  const inputCost = (inputTokens / 1_000_000) * pricing.input;
  const outputCost = (outputTokens / 1_000_000) * pricing.output;
  // Note: Reasoning tokens may have same pricing as output tokens
  const reasoningCost = reasoningTokens > 0 ? (reasoningTokens / 1_000_000) * pricing.output : 0;
  
  return inputCost + outputCost + reasoningCost;
};

export const calculateTotalInputTokens = (
  text: string,
  options?: {
    conversationHistoryLength?: number;
    databaseCount?: number;
  }
): number => {
  const userInputTokens = estimateTokens(text);
  const systemOverhead = OVERHEAD_ESTIMATES.SYSTEM_MESSAGE;
  const mcpOverhead = OVERHEAD_ESTIMATES.MCP_TOOLS;
  const dbOverhead = OVERHEAD_ESTIMATES.DATABASE_INSTRUCTION_BASE + 
    (options?.databaseCount || 0) * OVERHEAD_ESTIMATES.DATABASE_INSTRUCTION_PER_DB;
  const conversationOverhead = (options?.conversationHistoryLength || 0) * 
    OVERHEAD_ESTIMATES.CONVERSATION_MESSAGE_OVERHEAD;
  
  return userInputTokens + systemOverhead + mcpOverhead + dbOverhead + conversationOverhead;
};

export const formatTokenCount = (tokens: number, isEstimate: boolean = false): string => {
  const prefix = isEstimate ? '~' : '';
  return `${prefix}${tokens.toLocaleString()} tokens`;
};

export const formatCost = (cost: number, isEstimate: boolean = false): string => {
  const prefix = isEstimate ? '~' : '';
  if (cost < 0.0001) return `${prefix}<$0.0001`;
  if (cost < 0.01) return `${prefix}$${cost.toFixed(4)}`;
  if (cost < 1) return `${prefix}$${cost.toFixed(3)}`;
  return `${prefix}$${cost.toFixed(2)}`;
};
